/**
 * The short tone a vehicle event plays when the user has unmuted its type.
 *
 * Synthesised with Web Audio rather than shipped as a file, so there is nothing to fetch or cache.
 * One `AudioContext` is created lazily and reused — browsers cap how many a page may open. Until
 * the page has had a user gesture the context stays suspended and the beep is silently skipped;
 * like `logActivity()`, this never throws, since a missed sound must not break the listener.
 */

const FREQUENCY_HZ = 880;
const DURATION_S = 0.15;
const PEAK_GAIN = 0.2;
/** A burst of batches (a tab waking up) beeps once rather than stuttering. */
const MIN_INTERVAL_MS = 500;

let context: AudioContext | null = null;
let lastPlayedAt = 0;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (context) return context;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context = new Ctor();
  return context;
}

function tone(ctx: AudioContext) {
  const start = ctx.currentTime;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = FREQUENCY_HZ;
  // Ramped in and out; a hard start or stop clicks audibly.
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + DURATION_S);
  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + DURATION_S);
}

export function playNotificationBeep() {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return;
  try {
    const ctx = getContext();
    if (!ctx) return;
    lastPlayedAt = now;
    if (ctx.state === "suspended") {
      // Without a gesture yet, Chrome leaves this promise pending until the next click — by then
      // the event is old news, so a late resume plays nothing.
      ctx.resume().then(
        () => {
          if (Date.now() - now < MIN_INTERVAL_MS) tone(ctx);
        },
        () => {},
      );
      return;
    }
    tone(ctx);
  } catch {
    // No audio device, or blocked by the browser — nothing to do.
  }
}
